import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";

const SearchCtx = createContext(null);
const STORE = "mhh_last_search";

export function SearchProvider({ children }) {
  const [origin, setOrigin] = useState(null);
  const [venues, setVenues] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const timer = useRef(null);
  const tries = useRef(0);

  const run = useCallback(async (params) => {
    clearTimeout(timer.current);
    setError("");
    try {
      const { data } = await api.get("/venues/search", { params });
      setOrigin(data.origin);
      setVenues(data.venues);
      setStatus(data.status);
      const next = { lat: data.origin.lat, lng: data.origin.lng, city: data.origin.city, state: data.origin.state };
      localStorage.setItem(STORE, JSON.stringify(next));
      if (data.status === "loading" && tries.current < 40) {
        tries.current += 1;
        timer.current = setTimeout(() => run(next), 4000);
      }
    } catch (e) {
      setStatus("error");
      setError(errMsg(e));
    }
  }, []);

  const searchCity = useCallback((city, state) => {
    tries.current = 0;
    setStatus("loading");
    setVenues([]);
    run({ city, state });
  }, [run]);

  const searchGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("error");
      setError("Your browser can't share location. Type a city instead.");
      return;
    }
    tries.current = 0;
    setStatus("locating");
    setVenues([]);
    navigator.geolocation.getCurrentPosition(
      (p) => { setStatus("loading"); run({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => { setStatus("error"); setError("Location access was blocked. Type your city and state instead."); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [run]);

  useEffect(() => {
    const saved = localStorage.getItem(STORE);
    if (saved) {
      setStatus("loading");
      run(JSON.parse(saved));
    }
    return () => clearTimeout(timer.current);
  }, [run]);

  return (
    <SearchCtx.Provider value={{ origin, venues, status, error, searchCity, searchGPS }}>{children}</SearchCtx.Provider>
  );
}

export const useSearch = () => useContext(SearchCtx);
